
import { MessageCore } from './messageCore';
import { MessageCache } from './messageCache';
import { MessageCounter } from './messageCounter';
import { MessageRealtime } from './messageRealtime';

export type { UnreadMessagesResponse } from './messageCounter';

/**
 * Main message service that orchestrates all message-related operations.
 * Provides a unified API for message functionality while delegating
 * specific responsibilities to focused modules.
 */
export class MessageService {
  /**
   * Gets the total unread message count for the current user.
   * Uses caching to improve performance.
   */
  static async getUnreadMessageCount(): Promise<number> {
    return MessageCounter.getUnreadMessageCount();
  }

  /**
   * Gets detailed unread messages information including per-conversation breakdown.
   */
  static async getUnreadMessagesDetail(): Promise<import('./messageCounter').UnreadMessagesResponse> {
    return MessageCounter.getUnreadMessagesDetail();
  }

  /**
   * Marks a specific message as read and invalidates cache.
   */
  static async markMessageAsRead(messageId: string): Promise<void> {
    return MessageCore.markMessageAsRead(messageId);
  }

  /**
   * Marks all unread messages in a conversation as read and invalidates cache.
   */
  static async markConversationAsRead(conversationId: string): Promise<void> {
    return MessageCore.markConversationAsRead(conversationId);
  }

  /**
   * Clears the message count cache.
   */
  static clearCache(): void {
    MessageCache.clearCache();
  }

  /**
   * Sets up real-time subscription for message updates.
   * @param callback Function to call when unread count changes
   * @returns Cleanup function to remove the subscription
   */
  static setupRealtimeSubscription(callback: (count: number) => void): () => void {
    return MessageRealtime.setupRealtimeSubscription(callback);
  }
}
